import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API_BASE } from "../../config";

import {
  Search,
  Lock,
  Download,
  Eye,
  Crown,
  Calendar,
  Sparkles,
} from "lucide-react";

const MonthlyMagazine = () => {
  const [magazines, setMagazines] = useState([]);
  const [openYears, setOpenYears] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  const user = JSON.parse(localStorage.getItem("user"));

  const isPremiumUser = user?.hasPaid;

  const loadRazorpay = (src) =>
  new Promise((resolve) => {
    const script = document.createElement("script");

    script.src = src;

    script.onload = () => resolve(true);

    script.onerror = () => resolve(false);

    document.body.appendChild(script);
  });

  const handleUpgrade = async () => {

  const loaded = await loadRazorpay(
    "https://checkout.razorpay.com/v1/checkout.js"
  );

  if (!loaded) {
    alert("Razorpay SDK failed to load");
    return;
  }

  try {

    const orderRes = await axios.post(
      `${API_BASE}/api/payments/create-order`,
      {
        amount: 399900,
        type: "notesPremium",
      },
      {
        headers: {
          Authorization: `Bearer ${user.token}`,
        },
      }
    );

    const { order } = orderRes.data;

    const options = {
      key: process.env.REACT_APP_RAZORPAY_KEY,

      amount: order.amount,

      currency: order.currency,

      name: "UPSC Journey",

      description: "Premium Membership",

      order_id: order.id,

      handler: async function (response) {

        try {

          await axios.post(
            `${API_BASE}/api/payments/verify-payment`,
            {
              razorpay_order_id: order.id,

              razorpay_payment_id:
                response.razorpay_payment_id,

              razorpay_signature:
                response.razorpay_signature,

              type: "notesPremium",
            },
            {
              headers: {
                Authorization: `Bearer ${user.token}`,
              },
            }
          );

          alert(
            "✅ Payment Successful! Premium Activated"
          );

          // Update local user
          const updatedUser = {
            ...user,
            hasPaid: true,
          };

          localStorage.setItem(
            "user",
            JSON.stringify(updatedUser)
          );

          window.location.reload();

        } catch (err) {

          console.error(err);

          alert("Payment verification failed");
        }
      },

      prefill: {
        email: user.email,
        name: user.username,
      },

      theme: {
        color: "#2563eb",
      },
    };

    const paymentObject =
      new window.Razorpay(options);

    paymentObject.open();

  } catch (err) {

    console.error(err);

    alert("Payment failed. Try again.");
  }
};

  // =========================
  // Fetch Magazines
  // =========================
  useEffect(() => {
    const fetchMagazines = async () => {
      try {
        const res = await axios.get(
          `${API_BASE}/api/monthly-magazines/all`
        );

        setMagazines(res.data.magazines || []);
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    };

    fetchMagazines();
  }, []);

  // =========================
// Track Magazine Analytics
// =========================

const trackMagazineActivity =
  async (magazineId) => {

    try {

      const token =
        JSON.parse(
          localStorage.getItem("user")
        )?.token;

      if (!token) return;

      await axios.post(

        `${API_BASE}/api/monthly-magazines/track-read`,

        {
          magazineId,
        },

        {
          headers: {
            Authorization:
              `Bearer ${token}`,
          },
        }
      );

    } catch (err) {

      console.error(
        "Analytics tracking failed",
        err
      );
    }
  };

  // =========================
  // Search Filter
  // =========================
  const filteredMagazines = useMemo(() => {
    return magazines.filter((mag) => {
      const query = search.toLowerCase();

      return (
        mag.title.toLowerCase().includes(query) ||
        mag.month.toLowerCase().includes(query) ||
        String(mag.year).includes(query)
      );
    });
  }, [magazines, search]);

  const latestMagazine = [...magazines]
  .sort((a, b) => {
    return (
      new Date(`${b.month} 1, ${b.year}`) -
      new Date(`${a.month} 1, ${a.year}`)
    );
  })[0];

  const groupedMagazines = filteredMagazines.reduce(
  (acc, magazine) => {
    const year = magazine.year;

    if (!acc[year]) {
      acc[year] = [];
    }

    acc[year].push(magazine);

    return acc;
  },
  {}
);

const sortedYears = Object.keys(groupedMagazines)
  .sort((a, b) => Number(b) - Number(a));

  useEffect(() => {
  if (sortedYears.length > 0) {
    setOpenYears({
      [sortedYears[0]]: true,
    });
  }
}, [filteredMagazines.length]);

  return (
    <div className="min-h-screen text-white p-4 md:p-6">

      {/* Hero Section */}
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-r from-indigo-700 via-blue-700 to-cyan-700 p-8 md:p-10 mb-8 shadow-2xl">

        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-72 h-72 bg-white rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-0 w-72 h-72 bg-cyan-300 rounded-full blur-3xl" />
        </div>

        <div className="relative z-10 flex flex-col md:flex-row md:items-center md:justify-between gap-6">

          <div>
            <div className="flex items-center gap-3 mb-4">
              <Sparkles className="w-8 h-8 text-yellow-300" />

              <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight">
                Monthly Magazine
              </h1>
            </div>

            <p className="text-blue-100 max-w-2xl text-lg leading-relaxed">
              Access premium UPSC monthly current affairs magazines,
              revision compilations, analysis PDFs, and curated exam-ready resources.
            </p>
          </div>

          {/* Premium Card */}
          <div className="bg-white/10 backdrop-blur-lg border border-white/20 rounded-2xl p-5 min-w-[280px] shadow-xl">

            <div className="flex items-center gap-3 mb-3">
              <Crown className="text-yellow-300" />

              <h3 className="font-bold text-xl">
                Premium Access
              </h3>
            </div>

            <p className="text-sm text-blue-100 leading-relaxed">
              Current month magazines are available exclusively for premium users.
              Previous months remain free for everyone.
            </p>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="relative mb-8">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />

        <input
          type="text"
          placeholder="Search magazines by month, year, or title..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full bg-zinc-900 border border-zinc-700 rounded-2xl py-4 pl-12 pr-4 text-white outline-none focus:border-blue-500 transition"
        />
      </div>

      {/* Loading State */}
      {loading && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {[...Array(6)].map((_, index) => (
            <div
              key={index}
              className="bg-zinc-900 rounded-3xl overflow-hidden animate-pulse"
            >
              <div className="h-72 bg-zinc-800" />

              <div className="p-4 space-y-3">
                <div className="h-5 bg-zinc-800 rounded" />
                <div className="h-4 bg-zinc-800 rounded w-1/2" />
                <div className="h-10 bg-zinc-800 rounded-xl" />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Empty State */}
      {!loading && filteredMagazines.length === 0 && (
        <div className="bg-zinc-900 border border-zinc-800 rounded-3xl py-20 text-center">
          <h2 className="text-2xl font-bold mb-3">
            No Magazines Found
          </h2>

          <p className="text-gray-400">
            Try searching with another keyword.
          </p>
        </div>
      )}

      {/* Magazine Archive */}
{!loading && filteredMagazines.length > 0 && (
  <div className="space-y-10">

    {sortedYears.map((year) => {

      const isOpen = openYears[year];

      return (
        <div
          key={year}
          className="bg-zinc-900/50 border border-zinc-800 rounded-3xl overflow-hidden"
        >

          {/* Year Header */}
          <button
            onClick={() =>
              setOpenYears((prev) => ({
                ...prev,
                [year]: !prev[year],
              }))
            }
            className="w-full flex items-center justify-between px-6 py-5 bg-gradient-to-r from-zinc-900 to-zinc-800 hover:from-zinc-800 hover:to-zinc-700 transition"
          >

            <div className="flex items-center gap-4">
              <div className="bg-blue-600 text-white px-4 py-2 rounded-xl font-bold text-lg">
                {year}
              </div>

              <p className="text-gray-300">
                {groupedMagazines[year].length} Magazine
                {groupedMagazines[year].length > 1 ? "s" : ""}
              </p>
            </div>

            <div className="text-2xl text-white">
              {isOpen ? "−" : "+"}
            </div>
          </button>

          {/* Year Content */}
          {isOpen && (
            <div className="p-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-7">

              {groupedMagazines[year].map(
                (magazine) => {

                  const now = new Date();

const isLatestMagazine =
  latestMagazine?._id === magazine._id;

const locked =
  isLatestMagazine &&
  !isPremiumUser;

                  return (
                    <div
                      key={magazine._id}
                      className="group relative bg-gradient-to-b from-zinc-900 to-black border border-zinc-800 rounded-3xl overflow-hidden shadow-xl hover:shadow-blue-500/10 transition-all duration-500 hover:-translate-y-2"
                    >

                      {/* Premium Badge */}
                      {isLatestMagazine && (
                        <div className="absolute top-4 right-4 z-20 bg-yellow-400 text-black text-xs font-bold px-3 py-1 rounded-full shadow-lg flex items-center gap-1">
                          <Crown className="w-3 h-3" />
                          PREMIUM
                        </div>
                      )}

                      {/* Thumbnail */}
                      <div className="relative overflow-hidden h-80">

                        <img
                          src={magazine.thumbnail}
                          alt={magazine.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                        />

                        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/20 to-transparent" />

                        {locked && (
                          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm flex flex-col items-center justify-center text-center p-5">

                            <Lock className="w-12 h-12 text-yellow-400 mb-4" />

                            <h3 className="font-bold text-xl mb-2">
                              Premium Only
                            </h3>

                            <p className="text-gray-300 text-sm mb-4">
                              Upgrade to access the latest monthly magazine.
                            </p>

                            <button
  onClick={handleUpgrade}
  className="bg-yellow-400 hover:bg-yellow-300 text-black font-bold px-5 py-2 rounded-xl transition"
>
  Upgrade Now
</button>
                          </div>
                        )}
                      </div>

                      {/* Content */}
                      <div className="p-5">

                        <div className="flex items-center gap-2 text-blue-400 text-sm mb-3">
                          <Calendar className="w-4 h-4" />
                          {magazine.month} {magazine.year}
                        </div>

                        <h2 className="text-lg font-bold leading-snug mb-5 line-clamp-2 min-h-[56px]">
                          {magazine.title}
                        </h2>

                        {!locked ? (
                          <div className="flex gap-3">

                            <a
  href={magazine.pdfUrl}

  target="_blank"

  rel="noreferrer"

  onClick={() =>
    trackMagazineActivity(
      magazine._id
    )
  }
                              className="flex-1 flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 py-3 rounded-xl font-semibold transition"
                            >
                              <Eye className="w-4 h-4" />
                              View
                            </a>

                            <a
  href={magazine.pdfUrl}

  download

  onClick={() =>
    trackMagazineActivity(
      magazine._id
    )
  }
                              className="flex items-center justify-center gap-2 bg-zinc-800 hover:bg-zinc-700 px-4 rounded-xl transition"
                            >
                              <Download className="w-4 h-4" />
                            </a>
                          </div>
                        ) : (
                          <button
                            disabled
                            className="w-full bg-zinc-800 text-gray-500 py-3 rounded-xl font-semibold cursor-not-allowed"
                          >
                            Locked Content
                          </button>
                        )}
                      </div>
                    </div>
                  );
                }
              )}
            </div>
          )}
        </div>
      );
    })}
  </div>
)}
 </div>
  );
};

export default MonthlyMagazine;